import * as React from 'react';

import { LucideEye, LucideEyeOff } from 'lucide-react';
import {
  Input as AriaInput,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
} from 'react-aria-components';

import { Maybe, cn, maybe } from '../../../lib';
import { IconButton } from '../buttons/IconButton';
import { FieldError, FieldGroup, Label } from '../Field';

/**
 * TextField props interface.
 * Extends AriaTextFieldProps and adds styling variants.
 */
export type TextFieldProps = AriaTextFieldProps & {
  /** Label text displayed above the input */
  label?: string;
  /** Inner label text displayed inside the input field */
  innerLabel?: string;
  /** Description text displayed below the label */
  description?: string;
  /** Error message shown when validation fails. Can be a string or a function that returns a string based on validation results */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  /** Placeholder to be set in the input. */
  placeholder?: string;
  /** Content to be rendered to the right of the label */
  labelAdornment?: React.ReactNode;
  /** Content to be rendered to the left of the inner label/placeholder */
  leftAdornment?: React.ReactNode;
  /** Content to be rendered to the left of the input */
  prefix?: React.ReactNode;
  /** Content to be rendered to the right of the input */
  suffix?: React.ReactNode;
  /** Number of rows to display in the textarea */
  rows?: number;
  /** Visual variant. Use 'bare' for borderless, transparent inputs */
  variant?: 'default' | 'bare';
  /** Enable auto-resize based on content using CSS field-sizing */
  autoResize?: boolean;
  /** Maximum height in pixels before scrolling (used with autoResize) */
  maxHeight?: number;

  /**
   * @deprecated use `rows`
   */
  textArea?: boolean;
};

export const TextField = ({
  className,
  label,
  innerLabel,
  description,
  errorMessage,
  placeholder,
  labelAdornment,
  leftAdornment,
  prefix,
  suffix,
  rows,
  textArea,
  variant,
  autoResize,
  maxHeight,
  ref,
  ...props
}: Omit<TextFieldProps, 'className' | 'value'> & {
  className?: string;
  value?: Maybe<string>;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const actualRows = rows ?? (textArea ? 2 : 1);
  const isTextArea = actualRows > 1;
  const isPassword = props.type === 'password';
  const [showPassword, setShowPassword] = React.useState(false);

  const isInvalid = props.isInvalid || !!errorMessage;

  const resolvedType = isPassword && showPassword ? 'text' : props.type;

  const passwordToggle = isPassword ? (
    <IconButton
      icon={
        showPassword ? (
          <LucideEyeOff className="size-4" />
        ) : (
          <LucideEye className="size-4" />
        )
      }
      variant="ghost"
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      onPress={() => setShowPassword((v) => !v)}
    />
  ) : null;

  const resolvedSuffix = isPassword ? (
    <>
      {suffix}
      {passwordToggle}
    </>
  ) : (
    suffix
  );

  return (
    <AriaTextField
      aria-label={'Text field'}
      className={composeRenderProps(className, (className) =>
        cn('@TextField', 'group flex flex-col gap-2', className),
      )}
      {...props}
      type={resolvedType}
      value={maybe(props.value)}
      isInvalid={isInvalid}
    >
      {(label || labelAdornment) && (
        <div className="flex flex-row items-center gap-2">
          {label && <Label aria-required={props.isRequired}>{label}</Label>}
          {labelAdornment}
        </div>
      )}
      <FieldGroup
        ref={ref}
        className={cn(
          isTextArea && variant !== 'bare' && 'h-auto p-0',
          isTextArea && variant !== 'bare' && 'min-h-[64px]',
          className,
        )}
        isDisabled={props.isDisabled}
        innerLabel={innerLabel}
        prefix={prefix}
        suffix={resolvedSuffix}
        variant={variant}
        prefixAlign={isTextArea && prefix ? 'start' : undefined}
      >
        {isTextArea ? (
          <AriaTextArea
            aria-label={label ?? 'label'}
            className={cn(
              variant === 'bare'
                ? 'w-full resize-none bg-transparent outline-none'
                : 'h-full min-h-[64px] px-2.5 py-2',
              autoResize && 'field-sizing-content',
            )}
            style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
            placeholder={placeholder}
            rows={rows}
          />
        ) : leftAdornment ? (
          <div className="flex flex-row items-center gap-2">
            {leftAdornment}
            <AriaInput
              aria-label={label ?? 'label'}
              placeholder={placeholder}
            />
          </div>
        ) : (
          <AriaInput aria-label={label ?? 'label'} placeholder={placeholder} />
        )}
      </FieldGroup>
      {description && (
        <div className="text-sm text-neutral/40">{description}</div>
      )}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </AriaTextField>
  );
};

export const TextArea = ({
  rows,
  className,
  variant,
  autoResize,
  maxHeight,
  ...props
}: Omit<TextFieldProps, 'className'> & {
  className?: string;
  rows?: number;
  variant?: 'default' | 'bare';
  autoResize?: boolean;
  maxHeight?: number;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <TextField
    {...props}
    rows={rows ?? 2}
    variant={variant}
    autoResize={autoResize}
    maxHeight={maxHeight}
    className={cn(className, '@TextArea')}
  />
);
